import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ActionButton } from './ActionButton';
import { responseZones } from '../data/responseZones';
import { describeDoseRule } from '../utils/rekovelleDose';
import { zoneFor } from '../utils/responseModel';
import type { DoseResult, PatientPreset } from '../types/simulation';
type ResultSummaryProps = {
  patient: PatientPreset;
  /** Null until the response has been run. */
  userN: number | null;
  currentLabel: string;
  isRekovelleUser: boolean;
  indiv: DoseResult;
  indivN: number;
  revealed: boolean;
  onReveal: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function ResultSummary({
  patient,
  userN,
  currentLabel,
  isRekovelleUser,
  indiv,
  indivN,
  revealed,
  onReveal
}: ResultSummaryProps) {
  if (userN === null) {
    return <div className="flex h-full flex-col justify-center rounded-xl border border-dashed border-rule px-6 py-10 text-center" data-id="element-278" data-mp="1v5a2oe335isz">
        <p className="font-serif text-2xl text-ink" data-id="element-279" data-mp="3ar12i7csimdk">Where will it land?</p>
        <p className="mx-auto mt-2 max-w-[260px] text-sm leading-relaxed text-graphite" data-id="element-280" data-mp="3arau1zdhh1ld">
          Run the response to see the tendency of your current approach for this patient.
        </p>
      </div>;
  }
  const userZone = zoneFor(userN);
  const user = responseZones[userZone];
  const ind = responseZones[zoneFor(indivN)];
  const note = userZone === 'over' ? 'May be associated with increased OHSS and intervention risk — not a certainty.' : userZone === 'under' && patient.amh < 15 ? 'Reserve-limited: added FSH yields little beyond the biological ceiling.' : null;
  return <div aria-live="polite" data-id="element-281" data-mp="2b7lzsiglawi">
      <AnimatePresence mode="wait" initial={false} data-id="element-282" data-mp="2az7xxalnzjq">
        {!revealed ? <motion.div key="result" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} data-id="element-283" data-mp="gaqtqk7cyk8a">
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${user.tintClass} ${user.textClass}`} data-id="element-284" data-mp="354x098uvkc86">
              {user.label}
            </span>
            <p className="mt-5 text-xs text-graphite" data-id="element-285" data-mp="3e4tudq01a3am">Your current choice · {currentLabel}</p>
            <p className={`mt-1 font-serif text-4xl leading-tight ${user.textClass}`} data-id="element-286" data-mp="3iv0htmit7hra">{user.tendency}</p>
            <p className="mt-5 border-t border-rule pt-4 text-[15px] text-ink" data-id="element-287" data-mp="3nun3mkcpe6bp">{user.relation}</p>
            {note && <p className="mt-1 text-sm text-graphite" data-id="element-288" data-mp="3ssye8xatokuz">{note}</p>}
            <div className="mt-7" data-id="element-289" data-mp="3svav174ugp6g">
              <ActionButton variant="go" onClick={onReveal} data-id="element-290" data-mp="p5l122d9s5v">
                See the REKOVELLE approach
              </ActionButton>
            </div>
          </motion.div> : <motion.div key="reveal" initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} data-id="element-291" data-mp="22hac6bc1klfj">
            <div className="border-b border-rule pb-4" data-id="element-292" data-mp="1zxe0xyzuxg8d">
              <p className="font-mono text-[10px] tracking-[0.16em] text-graphite/70" data-id="element-293" data-mp="1v65pkxeyvksk">YOUR CURRENT APPROACH</p>
              <p className="mt-1 font-serif text-2xl text-ink" data-id="element-294" data-mp="1q69k0tqmflaw">{currentLabel}</p>
              <p className={`mt-0.5 text-sm ${user.textClass}`} data-id="element-295" data-mp="1l7pzmxroehkn">{user.tendency}</p>
            </div>
            <p className="py-2 text-xs text-graphite" data-id="element-296" data-mp="2ivgg7m2lrysz">versus</p>
            <div className="border-b border-rule pb-4" data-id="element-297" data-mp="2iuoynf5uc1lp">
              <p className="font-mono text-[10px] tracking-[0.16em] text-accent" data-id="element-298" data-mp="2dvvgh9flzmf7">REKOVELLE INDIVIDUALIZED APPROACH</p>
              <p className="mt-1 font-serif text-2xl text-ink" data-id="element-299" data-mp="28w92uyvvc45v">
                AMH {patient.amh} pmol/L + {patient.weight} kg → <span className="text-accent" data-id="element-300" data-mp="24v5q4l94ao8v">{indiv.dose.toFixed(2)} μg</span>
              </p>
              <p className="mt-1 font-mono text-[11px] text-graphite" data-id="element-301" data-mp="xjoulytphj9z">{describeDoseRule(indiv, patient.weight)}</p>
              <p className={`mt-1 text-sm ${ind.textClass}`} data-id="element-302" data-mp="tf8qa1zqqjwv">{ind.tendency}</p>
            </div>

            {isRekovelleUser && <p className="mt-4 text-sm text-graphite" data-id="element-303" data-mp="2m3oipwxrzf3k">Your current choice already follows the individualized REKOVELLE algorithm.</p>}

            <p className="mt-5 font-serif text-xl leading-snug text-ink" data-id="element-304" data-mp="27gmzvqe0wgcu">Different patients can require different starting approaches.</p>
            <p className="mt-2 text-sm leading-relaxed text-graphite" data-id="element-305" data-mp="2cbsnctn2q331">
              REKOVELLE uses AMH + body weight in a defined dosing algorithm to individualize the starting dose from Day 1.
            </p>
            <p className="mt-3 text-sm font-medium text-ink" data-id="element-306" data-mp="2r3b814qvolsq">A defined patient-specific starting-dose approach.</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}